/**
 * CORE-1753 — «человек вернулся на платформу» для chat-api.
 *
 * Приветствие при возвращении (CORE-1452) запускалось только событием
 * `chat.opened`, и то лишь с веба. Кто вернулся, но чат не открыл, не получал
 * ничего, а кто открыл — получал приветствие ровно в момент открытия,
 * неотличимо от ответа на само открытие. В приложении Агапелла первой не
 * писала никогда.
 *
 * Теперь приложение сообщает о выходе на передний план отдельным событием
 * `app.opened`: при старте вошедшего приложения и при возвращении из фона
 * (натив — AppState → active, веб — вкладка снова видна). Решает, здороваться
 * ли, chat-api — по своим порогам (три часа, сутки, неделя) и по тумблеру
 * «Агапелла пишет первой». Здесь только сигнал и его частота.
 *
 * Почему свой предел частоты, раз у сервера уже есть порог в три часа. Порог
 * защищает от второго приветствия, но не от потока запросов: на iOS AppState
 * переключается и от шторки уведомлений, и от системного запроса Face ID, а
 * вкладку переключают десятки раз за час. Каждый такой сигнал — заход в базу и
 * вопрос бэкенду о настройке.
 *
 * CORE-1753, возврат QA 23.10 — возвращение отсчитывается от длины ухода, а не
 * от прошлого сигнала, и сигнал несёт эту длину (`away_s`): перерыв —
 * это время, пока приложения не было на экране, а видимая вкладка или
 * открытое приложение — это человек на платформе, даже если он ничего не
 * пишет (решение продукта через Fable, 23.10). См. `APP_OPENED_MIN_AWAY_MS`.
 *
 * Чистый модуль с подставляемыми зависимостями — как `unreadStore` рядом:
 * проверяется без React и без DOM.
 */

/** Не чаще раза в пять минут на человека. См. шапку — почему не больше. */
export const APP_OPENED_MIN_INTERVAL_MS = 5 * 60_000;

/**
 * CORE-1753, возврат QA 23.10 — сколько надо пробыть вне приложения, чтобы
 * выход на передний план считался возвращением.
 *
 * Раньше считался любой выход, и предел частоты отсчитывался от прошлого
 * сигнала, а не от длины ухода. Человек три часа в сеансе, на пять секунд
 * переключил вкладку — и сервер, который видел только запуск трёхчасовой
 * давности, писал «Привет, здорово что ты снова тут» посреди разговора.
 * Короткий уход — не возвращение, сигнала нет вовсе.
 *
 * От ошибочного приветствия защищает не этот порог, а длина ухода в сигнале
 * (`away_s`, см. `send`): с ней сервер считает перерыв не длиннее ухода. Порог
 * только не шлёт событие на каждое переключение вкладки. Почему не три часа,
 * как у сервера: тот же сигнал приносит предложение рефлексии после
 * Live-сессии и итог недели, им трёхчасовой уход не нужен (Fable, 23.10).
 */
export const APP_OPENED_MIN_AWAY_MS = 5 * 60_000;

/** Что вывело приложение на передний план — уходит в `props.trigger`. */
export type AppOpenedTrigger = 'launch' | 'foreground';

/** Когда приложение в последний раз ушло с переднего плана, и у кого. */
export interface LeftMark {
  userId: string;
  at: number;
}

/**
 * Где отметка ухода переживает перезапуск. Без неё холодный старт (закрыли
 * вкладку, система выгрузила приложение) не знает, сколько человека не было,
 * и перерыв считается от последней отметки сервера — то есть опять от
 * запуска, бывшего до трёхчасового сеанса.
 *
 * Отметка локальная, но решает по-прежнему сервер: длина ухода только
 * укорачивает его перерыв и никогда не удлиняет. На втором устройстве отметки
 * нет — там работает серверная, как и раньше.
 */
export interface LeftMarkStore {
  read: () => Promise<LeftMark | null>;
  write: (mark: LeftMark) => void;
}

export interface AppOpenedBeaconDeps {
  /** `awayMs` — сколько приложение было не на переднем плане; `null` — неизвестно. */
  send: (userId: string, trigger: AppOpenedTrigger, awayMs: number | null) => void;
  now?: () => number;
  minIntervalMs?: number;
  minAwayMs?: number;
  store?: LeftMarkStore;
}

export interface AppOpenedBeacon {
  /** Приложение запустилось на глазах у человека. `true` — сигнал ушёл. */
  launch: (userId: string) => Promise<boolean>;
  /** Приложение снова на переднем плане. `true` — сигнал ушёл. */
  foreground: (userId: string) => Promise<boolean>;
  /** Приложение ушло с переднего плана. */
  left: (userId: string) => void;
}

export function createAppOpenedBeacon(deps: AppOpenedBeaconDeps): AppOpenedBeacon {
  const {
    send,
    now = () => Date.now(),
    minIntervalMs = APP_OPENED_MIN_INTERVAL_MS,
    minAwayMs = APP_OPENED_MIN_AWAY_MS,
    store,
  } = deps;
  // Предел — на человека, а не на устройство: вышел из одного аккаунта и вошёл
  // в другой — это заход другого человека, и молчать о нём нельзя.
  let last: { userId: string; at: number } | null = null;
  // Уход этого запуска. Отдельно от хранилища: оно асинхронное в приложении и
  // может не ответить, а длину текущего ухода мы и так знаем.
  let leftHere: LeftMark | null = null;

  const storedLeftAt = async (userId: string): Promise<number | null> => {
    if (!store) return null;
    try {
      const mark = await store.read();
      return mark && mark.userId === userId ? mark.at : null;
    } catch {
      return null;
    }
  };

  /** Длина ухода; `null`, если отметки нет или часы ушли назад. */
  const awayFrom = (leftAt: number | null, at: number): number | null =>
    leftAt !== null && leftAt <= at ? at - leftAt : null;

  const rateLimited = (userId: string, at: number): boolean =>
    !!last && last.userId === userId && at - last.at < minIntervalMs;

  return {
    async launch(userId) {
      if (!userId) return false;
      const at = now();
      if (rateLimited(userId, at)) return false;
      last = { userId, at };
      send(userId, 'launch', awayFrom(await storedLeftAt(userId), at));
      return true;
    },
    async foreground(userId) {
      if (!userId) return false;
      const at = now();
      const mine = leftHere && leftHere.userId === userId ? leftHere.at : null;
      leftHere = null;
      // Из двух уходов — более поздний. Хранилище общее для вкладок: если
      // человек ушёл из соседней минуту назад, он был здесь минуту назад. И
      // оно же — единственное, что знает об уходе, если этот запуск его не
      // видел (стартовал в фоне).
      const stored = await storedLeftAt(userId);
      const leftAt = mine === null ? stored : stored === null ? mine : Math.max(mine, stored);
      const away = awayFrom(leftAt, at);
      if (away !== null && away < minAwayMs) return false;
      if (rateLimited(userId, at)) return false;
      last = { userId, at };
      send(userId, 'foreground', away);
      return true;
    },
    left(userId) {
      if (!userId) return;
      leftHere = { userId, at: now() };
      try {
        store?.write(leftHere);
      } catch {
        /* приватный режим без хранилища — останется серверная отметка */
      }
    },
  };
}

/** Передний план платформы — натив и веб отвечают на это по-разному. */
export interface ForegroundSource {
  /** Видит ли человек приложение прямо сейчас. */
  isForeground: () => boolean;
  /** Подписка на выход на передний план. Возвращает отписку. */
  onForeground: (listener: () => void) => () => void;
  /** CORE-1753 — подписка на уход с переднего плана. Возвращает отписку. */
  onBackground: (listener: () => void) => () => void;
}

/** Срез `document`, который здесь нужен. */
export interface VisibilityDocumentLike {
  visibilityState: string;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/** Срез `window` для `pagehide`. */
export interface PageHideTargetLike {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/**
 * Веб: вкладка снова видна / скрыта. Без `document` (SSR, тест без DOM) — `null`.
 *
 * Уход — это и закрытие вкладки, и перезагрузка: браузер скрывает страницу
 * перед выгрузкой, и следующий запуск узнаёт из хранилища, что человек был
 * здесь секунду назад. `pagehide` — подстраховка для браузеров, где при
 * выгрузке `visibilitychange` не приходит; две записи подряд безвредны.
 */
export function webForegroundSource(
  doc: VisibilityDocumentLike | null | undefined,
  win?: PageHideTargetLike | null,
): ForegroundSource | null {
  if (!doc) return null;
  return {
    isForeground: () => doc.visibilityState === 'visible',
    onForeground(listener) {
      const onChange = () => {
        if (doc.visibilityState === 'visible') listener();
      };
      doc.addEventListener('visibilitychange', onChange);
      return () => doc.removeEventListener('visibilitychange', onChange);
    },
    onBackground(listener) {
      const onChange = () => {
        if (doc.visibilityState !== 'visible') listener();
      };
      doc.addEventListener('visibilitychange', onChange);
      win?.addEventListener('pagehide', listener);
      return () => {
        doc.removeEventListener('visibilitychange', onChange);
        win?.removeEventListener('pagehide', listener);
      };
    },
  };
}

/** Срез `AppState` из react-native. */
export interface AppStateLike {
  currentState: string | null;
  addEventListener: (type: 'change', listener: (state: string) => void) => { remove: () => void };
}

/**
 * Натив: AppState → active. Срабатывает только на переходе в `active` из
 * другого состояния — повторный `change` с тем же значением возвращением не
 * является.
 *
 * Уход — переход из `active` в любое другое состояние, в том числе
 * `inactive` (iOS: шторка уведомлений, Face ID, переключатель приложений).
 * Такой уход длится секунды и короче порога, поэтому возвращением не
 * считается — тем же правилом, что пятисекундное переключение вкладки.
 */
export function nativeForegroundSource(appState: AppStateLike): ForegroundSource {
  return {
    isForeground: () => appState.currentState === 'active',
    onForeground(listener) {
      let previous = appState.currentState;
      const sub = appState.addEventListener('change', (next) => {
        const cameBack = next === 'active' && previous !== 'active';
        previous = next;
        if (cameBack) listener();
      });
      return () => sub.remove();
    },
    onBackground(listener) {
      let previous = appState.currentState;
      const sub = appState.addEventListener('change', (next) => {
        const wentAway = previous === 'active' && next !== 'active';
        previous = next;
        if (wentAway) listener();
      });
      return () => sub.remove();
    },
  };
}

/**
 * Запуск для вошедшего человека: сигнал о старте и подписка на уходы и
 * возвращения. Возвращает отписку — для очистки эффекта.
 *
 * Старт, пришедший в фоне (вкладку открыли фоновой, приложение подняла
 * система), сигналом не считается: человек ничего не видит. Он придёт с
 * первым выходом на передний план.
 */
export function startAppOpenedBeacon(p: {
  userId: string;
  source: ForegroundSource;
  beacon: AppOpenedBeacon;
}): () => void {
  const { userId, source, beacon } = p;
  if (source.isForeground()) void beacon.launch(userId);
  const offForeground = source.onForeground(() => {
    void beacon.foreground(userId);
  });
  const offBackground = source.onBackground(() => beacon.left(userId));
  return () => {
    offForeground();
    offBackground();
  };
}
